<template>
  <b-card
    v-if="data"
    no-body
    class="card-revenue-budget"
  >
    <b-row class="mx-0">
      <b-col
        md="12"
        class="revenue-report-wrapper"
      >
        <div class="d-sm-flex justify-content-between align-items-center mb-3">
          <h4 class="card-title mb-50 mb-sm-0 chart-title">
            {{ $t(title) }}
            <feather-icon
              v-if="$t(title) != 'Citywise Delivery Statistics'"
              v-b-tooltip.hover.v-primary="'This chart presents data from the past 12 months, providing an up-to-date snapshot of current trends and patterns.'"
              icon="HelpCircleIcon"
              size="15"
              class="cursor-pointer text-primary"
            />
          </h4>
          <b-button
            v-ripple.400="'rgba(113, 102, 240, 0.15)'"
            size="sm"
            variant="outline-primary"
            class="widgetrefresh-button d-sm-flex align-items-center justify-content-center shadow-custom"
            :style="cardBorder"
            @click="refreshWidget"
          >
            <feather-icon
              icon="RefreshCwIcon"
              class="mr-50 refresh-icon"
            />
            <span class="align-middle refresh-span" />
            <time-ago
              class="timeAgo"
              :datetime="lastUpdatedAt"
              refresh
              locale="en"
              :long="true"
              :style="{color: '#1C1F32'}"
            />
          </b-button>
        </div>

        <!-- chart -->
        <vue-apex-charts
          id="revenue-report-chart"
          type="bar"
          height="300"
          :options="revenueReport.chartOptions"
          :series="data.series"
        />
      </b-col>
    </b-row>
  </b-card>
</template>

<script>
import {
  BCard, BRow, BCol, VBTooltip, BButton,
} from 'bootstrap-vue'
import VueApexCharts from 'vue-apexcharts'
import Ripple from 'vue-ripple-directive'
import { TimeAgo } from 'vue2-timeago'
import RefreshMixin from '@/@core/mixins/ui/refresh'

export default {
  components: {
    VueApexCharts,
    BCard,
    BRow,
    BCol,
    TimeAgo,
    BButton,
  },
  directives: {
    'b-tooltip': VBTooltip,
    Ripple,
  },
  mixins: [RefreshMixin],
  props: {
    title: {
      type: String,
      default: null,
    },
    data: {
      type: Object,
      default: () => {},
    },
    lastUpdatedAt: {
      type: String,
      default: null,
    },
    queue: {
      type: String,
      required: true,
    },
    chartColor: {
      type: String,
      default: null, // Default to null so we can check if it was passed
    },
  },
  data() {
    return {
      revenueReport: {
        chartOptions: {
          chart: {
            height: 350,
            type: 'line',
            zoom: { enabled: false },
          },
          // plotOptions: {
          //   bar: {
          //     columnWidth: '2px', // Decrease this value for thinner bars
          //     borderRadius: 4, // Small radius for bar corners
          //   },
          // },
          fill: {
            type: 'gradient',
            gradient: {
              shade: 'light',
              type: 'vertical',
              shadeIntensity: 0.5,
              inverseColors: false,
              opacityFrom: 1,
              opacityTo: 1,
              stops: [0, 100],
              colorStops: [
                [
                  {
                    offset: 0,
                    color: '#0094FF', // top color for bar 1
                    opacity: 1,
                  },
                  {
                    offset: 100,
                    color: '#005999', // bottom color for bar 1
                    opacity: 1,
                  },
                ],
              ],
            },
          },
          dataLabels: {
            enabled: false,
          },
          stroke: {
            curve: 'straight',
          },
          grid: {
            borderColor: '#f1f1f1',
          },
          responsive: [
            {
              breakpoint: 5000,
              options: {
                xaxis: {
                  categories: this.data.categories.lables,
                  labels: {
                    show: true,
                    rotate: -65,
                  },
                },
              },
            },
            {
              breakpoint: 767,
              options: {
                xaxis: {
                  categories: this.data.categories.lables,
                  labels: {
                    show: true,
                    rotate: -75,
                    maxHeight: 210,
                  },
                },

              },
            },
          ],
          xaxis: {
            categories: this.data.categories.lables,
            labels: {
              show: true,
              rotate: 0,
              rotateAlways: false,
              hideOverlappingLabels: false,
              showDuplicates: false,
              trim: false,
              minHeight: undefined,
              maxHeight: 200,
              style: {
                colors: [],
                fontSize: '12px',
                fontFamily: 'Helvetica, Arial, sans-serif',
                fontWeight: 900,
                cssClass: 'apexcharts-xaxis-label',
              },
            },
          },
          yaxis: {
            tickAmount: 3,
            labels: {
              formatter(val) {
                return val.toLocaleString()
              },
            },
          },
        },
      },
    }
  },
  computed: {
    cardBorder() {
      return {
        border: '1px solid var(--primary) !important',
      }
    },
  },
  created() {
    // Check if the parent component passed a custom color
    if (this.chartColor) {
      this.revenueReport.chartOptions.fill = {
        type: 'solid', // Use a solid fill instead of a gradient
        colors: [this.chartColor], // Use the color passed in the prop
      }
    }
    let tooltipConfig
    let plotOptions
    if (this.title === 'Citywise Delivery Statistics') {
      // For Citywise chart, use a fixed, non-intersecting tooltip
      tooltipConfig = {
        enabled: true,
        custom({
          series, seriesIndex, dataPointIndex, w,
        }) {
          const value = series[seriesIndex][dataPointIndex]
          const cityLabel = w.globals.labels[dataPointIndex]

          return `
      <div style="
        border-radius: 10px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        min-width: 180px;
        font-family: Inter, Arial, sans-serif;
      ">
        <div style="
          padding: 8px 10px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 6px;
          font-size: 12px;
          font-weight: 600;
          color: #000;
          border-bottom: 1px solid rgba(255, 255, 255, 0.2);
        ">
          <span style="color: #214f1c">${cityLabel}</span>
          <span style="display:flex;align-items:center;gap:4px;">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="#2E7D32">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z"/>
            </svg>
            Colombo
          </span>
        </div>

        <div style="
          padding: 8px 10px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          background:#9bf493;
          color: #000
        ">
          <span style="font-size: 11px;">
            Delivered Orders
          </span>
          <span style="
            padding:2px 10px;
            font-size:12px;
          ">
            ${value.toLocaleString()}
          </span>
        </div>
      </div>
    `
        },
      }
      plotOptions = {
        bar: {
          columnWidth: '17%',
          borderRadius: 4,
        },
      }
    } else {
      // For all other charts, use the custom HTML tooltip
      tooltipConfig = {
        enabled: true,
        custom({ series, seriesIndex, dataPointIndex }) {
          const value = series[seriesIndex][dataPointIndex]
          return `
            <div
              class="custom-tooltip"
              style="
                padding: 2px 14px;
                background: #0094FF;
                color: #FFF;
                border-radius: 20px;
                font-size: 14px;
                font-weight: 600;
              "
            >
              <span>${value}</span>
            </div>
          `
        },
      }
      plotOptions = {
        bar: {
          columnWidth: '2px',
          borderRadius: 4,
        },
      }
    }
    this.revenueReport.chartOptions.tooltip = tooltipConfig
    this.revenueReport.chartOptions.plotOptions = plotOptions
  },
}
</script>
<style lang="scss" scoped>
@import '../@core/scss/vue/pages/widget-refresh.scss';
.refresh-icon {
  color: #1C1F32;
}
</style>
